import { useState } from 'react';
import { flushEmailQueue } from '@shared/email/flushQueue';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary } from '@shared/ui';
import { formatDate } from '@shared/lib/format';
import UserAccessDrawer from './UserAccessDrawer';

interface Row { id: string; display_name: string | null; email: string | null; organization_id: string | null; created_at: string | null; role: string | null; email_confirmed?: boolean | null }

const TYPE_LABEL: Record<string, string> = { customer: 'Бизнес', creator: 'Креатор', field_agent: 'Агент', admin: 'Админ', precustomer: 'Не завершил' };

/** In-progress funnel for one account type, shown above the active roster in its tab:
 *  - kind='business': brand claims (code issued) + company activations + abandoned brand signups.
 *  - kind='creator':  creator claims (code issued).
 *  Abandoned/unconfirmed signups are hidden behind a toggle (business only). */
export default function PendingAccounts({ kind = 'business' }: { kind?: 'business' | 'creator' }) {
  const { t } = useTranslation('admin');
  const [drawerUser, setDrawerUser] = useState<{ id: string; name: string | null } | null>(null);
  const [showAbandoned, setShowAbandoned] = useState(false);

  const { data, isLoading, error } = useSupabaseQuery<Row[]>(
    ['admin-pending-accounts'],
    async () => supabase.rpc('admin_pending_accounts'),
  );
  const invalidate = useInvalidateSupabase();
  const orgsQ = useSupabaseQuery<{ id: string; name: string }[]>(['admin-organizations'], async () => supabase.from('organizations').select('id, name'));
  const orgName = (id: string | null) => (id ? (orgsQ.data ?? []).find((o) => o.id === id)?.name ?? '—' : '—');

  // Pending BUSINESSES (company onboarding creates a pending org). Approving a request
  // WITH an Instagram handle creates a brand + IG + a self-verify claim but INTENTIONALLY
  // keeps the org pending (the user must post the code under the IG post). So the row must
  // switch to a "код выдан · ждёт подтверждения" state, not re-render the same button —
  // admin_pending_company_requests() reports the .approved flag + the issued code.
  type PendingOrg = { id: string; name: string; created_at: string | null; onboarding_handle: string | null; member_email: string | null; member_id: string | null; approved: boolean; code: string | null };
  const pendingOrgsQ = useSupabaseQuery<PendingOrg[]>(
    ['admin-pending-orgs'],
    async () => supabase.rpc('admin_pending_company_requests'),
  );
  const [busyOrg, setBusyOrg] = useState<string | null>(null);
  const [pickedSub, setPickedSub] = useState<Record<string, string>>({});

  // Subcategories for the "classify the brand on approval" picker (grouped by category).
  const subsQ = useSupabaseQuery<{ id: string; label: string; cat: string; catSort: number; subSort: number }[]>(
    ['admin-approve-subs'],
    async () => {
      const { data, error } = await supabase
        .from('subcategories')
        .select('id, name, name_ru, sort_order, categories!inner(name, name_ru, sort_order)')
        .eq('is_active', true);
      if (error) return { data: null, error } as never;
      // Supabase types the !inner join as an array; treat it loosely like BrandsReview does.
      const rows = (data ?? []).map((s: any) => {
        const c = Array.isArray(s.categories) ? s.categories[0] : s.categories;
        return {
          id: s.id as string,
          label: (s.name_ru || s.name) as string,
          cat: (c?.name_ru || c?.name || '—') as string,
          catSort: (c?.sort_order ?? 999) as number,
          subSort: (s.sort_order ?? 999) as number,
        };
      });
      rows.sort((a, b) => a.catSort - b.catSort || a.cat.localeCompare(b.cat, 'ru') || a.subSort - b.subSort || a.label.localeCompare(b.label, 'ru'));
      return { data: rows, error: null } as never;
    },
  );
  const subGroups = (subsQ.data ?? []).reduce<Record<string, { id: string; label: string }[]>>((acc, s) => {
    (acc[s.cat] ??= []).push({ id: s.id, label: s.label });
    return acc;
  }, {});

  const activateOrg = async (id: string) => {
    // Subcategory is mandatory (backend also enforces it). No-IG companies can't be
    // activated at all — the backend raises handle_required — so this path only runs
    // for orgs that have a handle, where a subcategory must be chosen.
    const subId = pickedSub[id] || null;
    if (!subId) { alert(t('pend.pickSubAlert', { defaultValue: 'Выберите подкатегорию бренда.' })); return; }
    setBusyOrg(id);
    const { error } = await supabase.rpc('admin_approve_company_request', { p_org: id, p_subcategory_id: subId });
    setBusyOrg(null);
    if (error) { alert(error.message); return; }
    void flushEmailQueue(); // requester gets the claim code by email right away
    invalidate(['admin-pending-orgs']); invalidate(['admin-orgs-list']); invalidate(['admin-pending-accounts']);
  };

  // Delete a brand user outright (with confirmation). Removes the account, its claims,
  // and — if it was the sole member of a still-pending org — that org too.
  const [busyDel, setBusyDel] = useState<string | null>(null);
  const deleteUserById = async (userId: string | null, label: string) => {
    if (!userId) { alert(t('pend.noUser', { defaultValue: 'Нет привязанного пользователя для удаления.' }) as string); return; }
    if (!confirm(t('pend.deleteConfirm', { defaultValue: 'Удалить пользователя {{e}} безвозвратно? Аккаунт и его заявки будут удалены.', e: label }) as string)) return;
    setBusyDel(userId);
    const { error } = await supabase.rpc('admin_delete_brand_user', { p_user: userId });
    setBusyDel(null);
    if (error) { alert(error.message); return; }
    invalidate(['admin-pending-accounts']); invalidate(['admin-orgs-list']); invalidate(['admin-pending-orgs']); invalidate(['admin-pending-claims']);
  };
  // Pending ownership claims (creator + brand): signed up + posted the code, awaiting the
  // comment auto-verify. Surfaced here so a just-registered creator (@djo1doshov) or a
  // direct brand claim (ticketonkg) isn't invisible between tabs, and so you can confirm
  // manually if the auto-check lags. Both kinds share one "awaiting confirmation" queue.
  type Claim = { id: string; kind: 'creator' | 'brand'; handle: string | null; code: string | null; email: string | null; name: string | null; created_at: string | null; user_id: string | null };
  const claimsQ = useSupabaseQuery<Claim[]>(
    ['admin-pending-claims'], async () => supabase.rpc('admin_pending_claims'));
  const claims = claimsQ.data ?? [];
  // This instance renders ONE type: brand claims under Businesses, creator claims under Creators.
  const shownClaims = claims.filter((c) => (kind === 'creator' ? c.kind === 'creator' : c.kind === 'brand'));
  // Emails already surfaced (with a code) in the claims list — dedupe them out of the
  // bare-account table so a claim-holder isn't listed twice.
  const claimEmails = new Set(claims.map((c) => (c.email ?? '').toLowerCase()).filter(Boolean));
  // Brand claims whose code is already shown (as an approved passive row) in the
  // "Компании ждут активации" card → dedupe: hide from the card, keep the actionable
  // confirm here.
  const brandClaimCodes = new Set(claims.filter((c) => c.kind === 'brand').map((c) => c.code).filter(Boolean));
  const [busyClaim, setBusyClaim] = useState<string | null>(null);
  const confirmClaim = async (c: Claim) => {
    if (!confirm(`Подтвердить владение вручную (минуя авто-проверку комментария под постом)?\n\n${c.kind === 'brand' ? c.name ?? '' : '@' + (c.handle ?? '')}`)) return;
    setBusyClaim(c.id);
    const rpc = c.kind === 'brand' ? 'confirm_brand_claim' : 'creator_claim_confirm';
    const { error } = await supabase.rpc(rpc, { p_claim_id: c.id });
    setBusyClaim(null);
    if (error) { alert(error.message); return; }
    // Fire the profile scrape right away (fire-and-forget) so the account is populated on
    // confirmation — not when they eventually open the app. Creators → their full profile;
    // brands → the brand's own IG profile (followers/avatar/verified). Both endpoints are
    // guarded (initial/recent + lock), so this is safe and at most one paid run.
    if (c.handle) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (token) void fetch(c.kind === 'brand' ? '/api/scrape-brand' : '/api/scrape-creator', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ handle: c.handle }),
        }).catch(() => {});
      } catch { /* best-effort */ }
    }
    invalidate(['admin-pending-claims']); invalidate(['admin-pending-orgs']); invalidate(['admin-orgs-list']); invalidate(['admin-creators-list']);
  };
  const rejectClaim = async (c: Claim) => {
    const reason = prompt('Причина отклонения (необязательно):') ?? '';
    setBusyClaim(c.id);
    const rpc = c.kind === 'brand' ? 'reject_advertiser_claim' : 'reject_creator_claim';
    const { error } = await supabase.rpc(rpc, { p_claim_id: c.id, p_reason: reason || null });
    setBusyClaim(null);
    if (error) { alert(error.message); return; }
    invalidate(['admin-pending-claims']);
  };

  // Companies belong to the business funnel only; hide company-cards already surfaced
  // (with an actionable confirm) in the claims queue.
  const pendingOrgs = kind === 'business'
    ? (pendingOrgsQ.data ?? []).filter((o) => !(o.approved && o.code && brandClaimCodes.has(o.code)))
    : [];
  // Bare abandoned/unconfirmed accounts (business funnel). Exclude anyone already shown
  // as a pending company or a claim-holder — show each person exactly once.
  const pendingOrgIds = new Set(pendingOrgs.map((o) => o.id));
  const rows = kind === 'business'
    ? (data ?? []).filter((r) =>
        (!r.organization_id || !pendingOrgIds.has(r.organization_id)) &&
        !claimEmails.has((r.email ?? '').toLowerCase()))
    : [];

  return (
    <SectionErrorBoundary name="admin-pending-accounts">
      {isLoading && <Skeleton height={200} />}
      {error && <div style={{ color: '#e74c3c' }}>{String((error as Error)?.message ?? error)}</div>}
      {/* No standalone empty-state — this funnel sits above the active roster in its tab. */}

      {shownClaims.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 'var(--fs-small)', color: '#7f9ad6', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>
            {t('pend.claims', { defaultValue: 'Выдан код — ждут подтверждения владения' })} · {shownClaims.length}
          </div>
          <div style={{ fontSize: 'var(--fs-caption)', color: '#8a93a6', marginBottom: 10 }}>
            {t('pend.claimsHint', { defaultValue: 'Пользователь получил код и подтверждает сам: комментарий под постом → авто-проверка. Ручное подтверждение не требуется — только если авто-проверка задерживается.' })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {shownClaims.map((c) => {
              const isBrand = c.kind === 'brand';
              return (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 12px', border: `1px solid ${isBrand ? 'rgba(201,154,58,0.3)' : 'rgba(74,158,255,0.3)'}`, background: isBrand ? 'rgba(201,154,58,0.05)' : 'rgba(74,158,255,0.05)', borderRadius: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--fs-body)', color: '#dde', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 'var(--fs-caption)', color: isBrand ? '#d8b667' : '#8ab4ff', border: `1px solid ${isBrand ? 'rgba(201,154,58,0.45)' : 'rgba(74,158,255,0.45)'}`, borderRadius: 5, padding: '1px 6px' }}>
                        {isBrand ? t('pend.kindBrand', { defaultValue: 'Бренд' }) : t('pend.kindCreator', { defaultValue: 'Креатор' })}
                      </span>
                      {isBrand && c.name && <span>{c.name}</span>}
                      {c.handle
                        ? <a href={`https://instagram.com/${c.handle.replace(/^@+/, '')}/`} target="_blank" rel="noreferrer" style={{ color: '#8ab4ff', textDecoration: 'none' }}>@{c.handle.replace(/^@+/, '')}</a>
                        : (!isBrand && '—')}
                    </div>
                    <div style={{ fontSize: 'var(--fs-caption)', color: '#8a93a6' }}>
                      {c.email ?? '—'}
                      {c.code ? <> · <code style={{ color: '#bcd0ff' }}>{c.code}</code></> : null}
                      {c.created_at ? ` · ${formatDate(c.created_at)}` : ''}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => deleteUserById(c.user_id, c.email ?? c.handle ?? c.id)} disabled={busyDel === c.user_id}
                      title={t('pend.delete', { defaultValue: 'Удалить пользователя' }) as string}
                      style={{ cursor: busyDel === c.user_id ? 'not-allowed' : 'pointer', fontFamily: 'inherit', padding: '6px 12px', borderRadius: 6, fontSize: 'var(--fs-small)', color: '#c98', background: 'transparent', border: '1px solid rgba(231,76,60,0.3)', opacity: busyDel === c.user_id ? 0.45 : 1 }}>
                      {t('pend.delete', { defaultValue: 'Удалить' })}
                    </button>
                    <button type="button" onClick={() => rejectClaim(c)} disabled={busyClaim === c.id}
                      style={{ cursor: busyClaim === c.id ? 'not-allowed' : 'pointer', fontFamily: 'inherit', padding: '6px 12px', borderRadius: 6, fontSize: 'var(--fs-small)', color: '#e6a2a2', background: 'transparent', border: '1px solid rgba(231,76,60,0.4)', opacity: busyClaim === c.id ? 0.45 : 1 }}>
                      {t('pend.reject', { defaultValue: 'Отклонить' })}
                    </button>
                    <button type="button" onClick={() => confirmClaim(c)} disabled={busyClaim === c.id}
                      title="Подтверждает владение вручную (минуя авто-проверку комментария)."
                      style={{ cursor: busyClaim === c.id ? 'not-allowed' : 'pointer', fontFamily: 'inherit', padding: '6px 14px', borderRadius: 6, fontSize: 'var(--fs-small)', fontWeight: 600, color: '#0d1117', background: '#2ecc71', border: 'none', opacity: busyClaim === c.id ? 0.45 : 1 }}>
                      {busyClaim === c.id ? '…' : t('pend.confirm', { defaultValue: 'Подтвердить' })}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pendingOrgs.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 'var(--fs-small)', color: '#c99a3a', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>
            {t('pend.orgs', { defaultValue: 'Компании ждут активации' })} · {pendingOrgs.length}
          </div>
          <div style={{ fontSize: 'var(--fs-caption)', color: '#8a93a6', marginBottom: 10 }}>
            {t('pend.orgsHint', { defaultValue: 'Ждут вашего одобрения: выберите подкатегорию и нажмите «Одобрить». Без Instagram активация невозможна — запросите хэндл.' })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingOrgs.map((o) => (
              <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid rgba(241,196,15,0.3)', background: 'rgba(241,196,15,0.05)', borderRadius: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--fs-body)', color: '#dde' }}>{o.name}</div>
                  <div style={{ fontSize: 'var(--fs-caption)', color: '#8a93a6' }}>
                    {o.onboarding_handle
                      ? <>IG: <a href={`https://instagram.com/${o.onboarding_handle.replace(/^@+/, '')}/`} target="_blank" rel="noreferrer" style={{ color: '#8ab4ff' }}>@{o.onboarding_handle.replace(/^@+/, '')}</a></>
                      : t('pend.noIg', { defaultValue: 'Заявка без Instagram' })}
                    {o.member_email ? ` · ${o.member_email}` : ''}
                    {o.created_at ? ` · ${formatDate(o.created_at)}` : ''}
                  </div>
                  {o.approved && o.code && (
                    <div style={{ fontSize: 'var(--fs-caption)', color: '#7fd18f', marginTop: 3 }}>
                      {t('pend.codeIssued', { defaultValue: 'Код выдан пользователю' })}: <code style={{ color: '#bfe8c8' }}>{o.code}</code>
                    </div>
                  )}
                </div>
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                {o.approved ? (
                  <span style={{ flexShrink: 0, fontSize: 'var(--fs-small)', fontWeight: 600, color: '#7fd18f', padding: '6px 12px', border: '1px solid rgba(46,204,113,0.4)', borderRadius: 6, background: 'rgba(46,204,113,0.08)', whiteSpace: 'nowrap' }}
                    title="Бренд создан, код выдан. Организация активируется автоматически, когда пользователь подтвердит владение через комментарий под постом.">
                    ✓ {t('pend.awaitingUser', { defaultValue: 'Ждём подтверждения' })}
                  </span>
                ) : !o.onboarding_handle ? (
                  // No Instagram → can't be activated (brands require a handle now). Ask
                  // the user for a handle, or delete the request via the account queue below.
                  <span style={{ flexShrink: 0, fontSize: 'var(--fs-small)', color: '#e6a35a', padding: '6px 12px', border: '1px solid rgba(230,163,90,0.4)', borderRadius: 6, background: 'rgba(230,163,90,0.08)', whiteSpace: 'nowrap' }}
                    title="У бренда нет Instagram — активация невозможна. Запросите хэндл у пользователя или удалите заявку.">
                    {t('pend.needHandle', { defaultValue: 'Нет Instagram — нужен хэндл' })}
                  </span>
                ) : (
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <select
                      value={pickedSub[o.id] ?? ''}
                      onChange={(e) => setPickedSub((m) => ({ ...m, [o.id]: e.target.value }))}
                      title={t('pend.pickSub', { defaultValue: 'Подкатегория бренда' }) as string}
                      style={{ maxWidth: 200, padding: '6px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', fontFamily: 'inherit', color: '#dde', background: '#0f1627', border: '1px solid rgba(74,158,255,0.35)' }}>
                      <option value="">{t('pend.pickSub', { defaultValue: '— Подкатегория бренда —' })}</option>
                      {Object.entries(subGroups).map(([cat, subs]) => (
                        <optgroup key={cat} label={cat}>
                          {subs.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    <button type="button" onClick={() => activateOrg(o.id)}
                      disabled={busyOrg === o.id || !pickedSub[o.id]}
                      title="Создаст бренд + IG (с выбранной подкатегорией) и выдаст код самоверификации пользователю"
                      style={{ cursor: (busyOrg === o.id || !pickedSub[o.id]) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', padding: '6px 14px', borderRadius: 6, fontSize: 'var(--fs-small)', fontWeight: 600, color: '#0d1117', background: '#2ecc71', border: 'none', opacity: (busyOrg === o.id || !pickedSub[o.id]) ? 0.45 : 1 }}>
                      {busyOrg === o.id ? '…' : t('pend.approve', { defaultValue: 'Одобрить' })}
                    </button>
                  </div>
                )}
                <button type="button" onClick={() => deleteUserById(o.member_id, o.member_email ?? o.name)} disabled={busyDel === o.member_id}
                  title={t('pend.delete', { defaultValue: 'Удалить пользователя' }) as string}
                  style={{ cursor: busyDel === o.member_id ? 'not-allowed' : 'pointer', fontFamily: 'inherit', padding: '6px 12px', borderRadius: 6, fontSize: 'var(--fs-small)', color: '#c98', background: 'transparent', border: '1px solid rgba(231,76,60,0.3)', opacity: busyDel === o.member_id ? 0.45 : 1 }}>
                  {t('pend.delete', { defaultValue: 'Удалить' })}
                </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <button type="button" onClick={() => setShowAbandoned((s) => !s)}
            style={{ cursor: 'pointer', fontFamily: 'inherit', background: 'none', border: 0, color: '#8a93a6', fontSize: 'var(--fs-small)', padding: '4px 0', marginBottom: showAbandoned ? 10 : 0, textDecoration: 'underline' }}>
            {showAbandoned
              ? t('pend.hideAbandoned', { defaultValue: 'Скрыть недорегистрации' })
              : t('pend.showAbandoned', { defaultValue: 'Показать недорегистрации ({{n}})', n: rows.length })}
          </button>
          {showAbandoned && (
          <>
          <div style={{ fontSize: 'var(--fs-caption)', color: '#8a93a6', margin: '2px 0 8px' }}>
            {t('pend.abandonedHint', { defaultValue: 'Зарегистрировались, но не начали заявку на бренд. «Email не подтверждён» — так и не вошли; остальные вошли, но не подключили компанию.' })}
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-body)' }}>
              <thead style={{ background: 'rgba(15,45,100,0.2)' }}>
                <tr>
                  <th style={th}>{t('users.displayName', { defaultValue: 'Имя / email' })}</th>
                  <th style={th}>{t('users.type', { defaultValue: 'Тип' })}</th>
                  <th style={th}>{t('biz.company', { defaultValue: 'Компания' })}</th>
                  <th style={th}>{t('users.joined', { defaultValue: 'Регистрация' })}</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const role = r.role ?? 'precustomer';
                  const showName = r.display_name && r.display_name !== r.email;
                  // The "тип" here is really funnel status: S0 (never confirmed email,
                  // never got in) vs S1 (signed in, just didn't connect a company).
                  const noEmail = r.email_confirmed === false;
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid rgba(15,45,100,0.3)' }}>
                      <td style={td}>
                        {showName && <div style={{ color: '#dde' }}>{r.display_name}</div>}
                        <div style={{ color: showName ? '#8a93a6' : '#dde', fontSize: showName ? 'var(--fs-caption)' : undefined }}>{r.email ?? '—'}</div>
                      </td>
                      <td style={{ ...td }}>
                        {noEmail
                          ? <span style={{ fontSize: 'var(--fs-caption)', color: '#e6a35a', border: '1px solid rgba(230,163,90,0.4)', borderRadius: 5, padding: '1px 7px', whiteSpace: 'nowrap' }}>{t('pend.noEmail', { defaultValue: 'Email не подтверждён' })}</span>
                          : <span style={{ color: '#9aa' }}>{TYPE_LABEL[role] ?? role}</span>}
                      </td>
                      <td style={{ ...td, color: '#cbd5ff' }}>{orgName(r.organization_id)}</td>
                      <td style={{ ...td, color: '#888' }}>{r.created_at ? formatDate(r.created_at) : '—'}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button type="button" onClick={() => setDrawerUser({ id: r.id, name: r.display_name })}
                            style={{ cursor: 'pointer', fontFamily: 'inherit', padding: '5px 12px', borderRadius: 6, fontSize: 'var(--fs-small)', color: '#bcd0ff', background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.4)' }}>
                            {t('pend.review', { defaultValue: 'Рассмотреть' })}
                          </button>
                          <button type="button" onClick={() => deleteUserById(r.id, r.email ?? r.id)} disabled={busyDel === r.id}
                            title={t('pend.delete', { defaultValue: 'Удалить пользователя' }) as string}
                            style={{ cursor: busyDel === r.id ? 'not-allowed' : 'pointer', fontFamily: 'inherit', padding: '5px 12px', borderRadius: 6, fontSize: 'var(--fs-small)', color: '#e6a2a2', background: 'transparent', border: '1px solid rgba(231,76,60,0.4)', opacity: busyDel === r.id ? 0.45 : 1 }}>
                            {busyDel === r.id ? '…' : t('pend.delete', { defaultValue: 'Удалить' })}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
          )}
        </>
      )}

      {drawerUser && <UserAccessDrawer userId={drawerUser.id} displayName={drawerUser.name} onClose={() => setDrawerUser(null)} />}
    </SectionErrorBoundary>
  );
}

const th: React.CSSProperties = { padding: 10, textAlign: 'left' };
const td: React.CSSProperties = { padding: 10 };
